import * as React from 'react';
import {Flag, Modal, Label, Input, TextArea} from "semantic-ui-react";
import ModalTextArea from "./ModalTextArea";
import {IAiMeta, IDataTwoLanguageInput, IVirtualizedTableCellComponentProps} from "../../VirtualizedTableInterfaces";
import AutoTextArea from "react-textarea-autosize";
import {CSSProperties} from "react";
import ModalShortTextArea from './ModalShortTextArea';
import HATEOASClient from 'src/services/HATEOASClient';
import {ResourceListOfOptionDto} from 'src/services/Client';

interface TwoLanguageInputProps extends IVirtualizedTableCellComponentProps {
    isNewGrid?: boolean;
    client?: HATEOASClient;
    postOptions?: ResourceListOfOptionDto;
    apiMethod?: (client: HATEOASClient, postOptions: ResourceListOfOptionDto) => { execute: (payload: any) => Promise<any> };
    grammarApiMethod?: (client: HATEOASClient, postOptions: ResourceListOfOptionDto) => {execute: (payload: any) => Promise<any>};
    aiGeneratedShorttext?:boolean;
    pslAiGeneratedShorttext?:boolean;
}

interface TwoLanguageInputState {
    value: IDataTwoLanguageInput | null;
    keyLanguage: string;
    closeOnDimmerClick: boolean;
    // used to limit the expansion of textarea when typing in
    // the field has the same name as the language key, for easier dynamic access
    de: number;
    gb: number;
    // flag used to re-trigger the render of textarea
    // so that it computes again the height after the text was updated by user
    hideTextarea: boolean;
}

export default class TwoLanguageInput extends React.Component<TwoLanguageInputProps, TwoLanguageInputState> {

    constructor(props: TwoLanguageInputProps) {
        super(props);
        this.state = {
            value: null,
            keyLanguage: 'de',
            closeOnDimmerClick: true,
            de: 0,
            gb: 0,
            hideTextarea: false,
        };
    }

    componentDidMount() {
        const value: IDataTwoLanguageInput = this.props.value;
        this.setState({value});
    }

    componentDidUpdate(prevProps: TwoLanguageInputProps) {
        const propsValue = this.props.value || "";
        const prevPropsValue = prevProps.value || "";
        if (propsValue !== prevPropsValue && this.state.value !== propsValue)
            this.setState({value: propsValue, hideTextarea: false});
    }

    private onValueChange = (key: string, value: string, aiMeta?: IAiMeta) => {
        this.setState({
            value: {
                ...this.state.value,
                [key]: {
                    // @ts-ignore
                    ...this.state.value[key],
                    value: value.replace(/(\r\n|\n|\r)/gm, ""),
                    aiMeta
                }
            }
        });
    }

    private saveChange = () => {
        const newValue: IDataTwoLanguageInput = this.state.value as IDataTwoLanguageInput;
        const oldValue: IDataTwoLanguageInput = this.props.value as IDataTwoLanguageInput;
        for (const key of Object.keys(oldValue)) {
            if (newValue[key].value !== oldValue[key].value) {
                this.setState({hideTextarea: true});
                this.props.onChange(newValue);
                break;
            }
        }
    }

    private onClose = () => {
        this.saveChange();
        if (this.props.clearActiveRow) this.props.clearActiveRow();
    }

    private onOpen = () => {
        if (this.props.setActiveRow) this.props.setActiveRow();
    }

    private onHeightChange = (key: string, height: number) => {
        // @ts-ignore already checked in render
        if (this.state.value[key].value === this.props.value[key].value) {
            // @ts-ignore key can only be 'gb' or 'de'
            this.setState({[key]: height});
        }
    }

    private getTextareaStyle = (key: string): CSSProperties => {
        // sometimes, especially when the width is small, AutoTextArea put a scrollbar on the field
        // even though it has enough space to grow
        // therefore, this 20 is used more like an error margin
        // @ts-ignore already checked in render
        const computedHeight = this.state[key] + (this.state.value[key].value === "" ? 0 : 20);
        return ({
            height: computedHeight,
            maxHeight: computedHeight,
            minHeight: computedHeight,
            width: '100%',
            marginBottom: key === 'de' ? 5 : 0,
            resize: 'none'
        });
    }

    public render() {
        const editingColumn = this.props.editDialogHeader ? this.props.editDialogHeader.editingColumn : '';
        const isShortTextColumn = editingColumn === "Shorttext" || "Kurztext";
        const isAIGenerated =
          isShortTextColumn && Boolean(this.props.aiGeneratedShorttext || this.props.pslAiGeneratedShorttext);

        if (!this.state.value) return null;

        if (this.props.uglyMode && !this.state.hideTextarea)
            return Object.keys(this.state.value).map(key =>
            (
                <AutoTextArea
                    key={"tli" + key}
                    onBlur={this.saveChange}
                    style={this.getTextareaStyle(key)}
                    // @ts-ignore already checked in first if
                    value={this.state.value[key].value || ""}
                    onChange={(e) => this.onValueChange(key, e.currentTarget.value)}
                    onHeightChange={(height) => this.onHeightChange(key, height)}
                    disabled={this.props.readOnly}
                />
            )
            );

        return (
          <Modal
            onClose={this.onClose}
            onOpen={this.onOpen}
            closeOnDimmerClick={this.state.closeOnDimmerClick}
            dimmer={'inverted'}
            trigger={
              <div className={isAIGenerated ? "ai-illuminated-border" : ""} style={{ display: "flex", flexDirection: "column" }}>
                {Object.keys(this.state.value).map((key) => {
                  // @ts-ignore already checked in first if
                  const lang = this.state.value[key].flag;

                  return (
                    <React.Fragment key={key}>
                      <Input
                        input={
                          <TextArea
                            rows={1}
                            className={`two-language-modal-trigger`}
                          />
                        }
                        onClick={() => {
                          this.setState({ keyLanguage: lang });
                        }}
                        disabled={this.props.readOnly}
                        label={
                          <Label basic={true} style={{ cursor: "pointer" }}>
                            <Flag name={lang} />
                          </Label>}
                        iconPosition='left'
                        style={{width: '100%', maxWidth: 'fill-available'}}
                        // @ts-ignore
                        value={this.state.value[key].value || ""}
                      />
                      <div className="disabled_input" />
                    </React.Fragment>
                  );
                })}
              </div>}
          >
            <ModalShortTextArea
              value={this.state.value}
              readOnly={this.props.readOnly}
              focusKey={this.state.keyLanguage}
              onChange={this.onValueChange}
              dialogHeader={this.props.editDialogHeader}
              closeOnDimmerClick={(value) =>
                this.state.closeOnDimmerClick !== value &&
                this.setState({ closeOnDimmerClick: value })
              }
              isNewGrid={this.props.isNewGrid ? true : false}
              client={this.props.client}
              postOptions={this.props.postOptions}
              apiMethod={this.props.apiMethod} // <-- Pass from parent!
              grammarApiMethod={this.props.grammarApiMethod}
            />
          </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/TwoLanguageInput.tsx